import * as Sentry from '@sentry/react';

/**
 * Initialize Sentry error tracking
 * Only runs in production or when explicitly enabled
 */
export function initSentry() {
    const dsn = process.env.REACT_APP_SENTRY_DSN;
    const isProduction = process.env.NODE_ENV === 'production';
    const forceEnable = process.env.REACT_APP_SENTRY_ENABLED === 'true';

    // Only initialize in production or when explicitly enabled
    if (!dsn || (!isProduction && !forceEnable)) {
        if (process.env.NODE_ENV === 'development') {
            console.debug('[Sentry] Disabled in development. Set REACT_APP_SENTRY_ENABLED=true to test.');
        }
        return;
    }

    Sentry.init({
        dsn,
        environment: process.env.NODE_ENV,
        release: process.env.REACT_APP_VERSION || 'unknown',

        // Sample rate for performance monitoring (adjust as needed)
        tracesSampleRate: isProduction ? 0.1 : 1.0,

        // Sample rate for session replays (optional, can be removed if not using)
        replaysSessionSampleRate: 0.0,
        replaysOnErrorSampleRate: isProduction ? 0.1 : 1.0,

        // Filter out noisy errors
        beforeSend(event, hint) {
            const error = hint?.originalException;

            // Ignore cancelled requests and network errors
            if (error?.name === 'AbortError') return null;
            if (error?.code === 'ERR_CANCELED') return null;

            // Ignore Firebase quota exceeded (already handled gracefully)
            if (error?.code === 'resource-exhausted') return null;

            // Ignore extension errors
            if (event?.exception?.values?.[0]?.stacktrace?.frames?.some(
                frame => frame?.filename?.includes('chrome-extension')
            )) {
                return null;
            }

            return event;
        },

        // Don't send errors from these URLs
        denyUrls: [
            /extensions\//i,
            /^chrome:\/\//i,
            /^moz-extension:\/\//i,
        ],
    });
}

/**
 * Capture an exception to Sentry with optional context
 */
export function captureException(error, context = {}) {
    if (process.env.NODE_ENV === 'development') {
        console.error('[Sentry] Would capture:', error, context);
    }

    Sentry.withScope(scope => {
        if (context.tags) {
            Object.entries(context.tags).forEach(([key, value]) => {
                scope.setTag(key, value);
            });
        }
        if (context.extra) {
            scope.setExtras(context.extra);
        }
        if (context.user) {
            scope.setUser(context.user);
        }
        if (context.level) {
            scope.setLevel(context.level);
        }
        Sentry.captureException(error);
    });
}

/**
 * Set user context for Sentry (call after login)
 */
export function setUser(user) {
    if (!user) {
        Sentry.setUser(null);
        return;
    }

    Sentry.setUser({
        id: user.uid,
        email: user.email,
        username: user.displayName || user.username
    });
}

/**
 * Clear user context (call on logout)
 */
export function clearUser() {
    Sentry.setUser(null);
}

// Re-export ErrorBoundary for use in components
export { ErrorBoundary } from '@sentry/react';
